import { useTranslation } from 'react-i18next';
import { Button, Grid, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import { AccountData, ExtendedDashboardOut } from '../../../api';
import { useAuth } from '../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../common/getDateAndTimeFormat';
import { ExtendedDashboardBox } from '../ExtendedDashboardBox';
import { SectionBreakdownTable } from './SectionBreakdownTable';
import { mb } from '../../../common/theme/mbTokens';

type Props = {
  data?: ExtendedDashboardOut;
  isLoading: boolean;
  lastFetch: moment.Moment;
  onRefresh: () => void;
};

export const SummaryTab = ({ data, isLoading, lastFetch, onRefresh }: Props) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { longDateFormat } = getDateFormat(account);

  return (
    <Grid container gap={2} sx={{ pb: 2 }}>
      <Grid container direction="column" sx={{ px: mb.layout.cardPadding }}>
        <Typography variant="h5">{t('analytics.summary.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('analytics.summary.subtitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('analytics.summary.hint')}
        </Typography>
        <Grid container alignItems="center" gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.lastUpdate', {
              time: `${lastFetch.format(longDateFormat)} ${lastFetch.format(getTimeFormat(account))}`,
            })}
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={onRefresh} disabled={isLoading} size="small">
            {t('dashboard.refresh')}
          </Button>
        </Grid>
      </Grid>

      <Grid container sx={{ px: mb.layout.cardPadding }}>
        <ExtendedDashboardBox
          isLoading={isLoading}
          title={t('analytics.summary.totalIncome')}
          today={data?.today?.total_income}
          yesterday={data?.yesterday?.total_income}
          month={data?.fromBeginningOfMonth?.total_income}
          previousMonth={data?.previousMonth?.total_income}
          sameMonthLastYear={data?.sameMonthLastYear?.total_income}
          isCurrency
          emphasized
        />
      </Grid>

      <SectionBreakdownTable data={data} isLoading={isLoading} />
    </Grid>
  );
};
